import { Vector3 as ThreeVector3 } from '@seddi/three';

import type { Sphere, Vector3 } from '../core/mathTypes';
import type { OrientedSphere } from '../core/orientedSphere';
import { degreeToRadian } from '../core/units';

export interface CameraLookAt {
  distance: number;
  origin: Vector3;
  target: Vector3;
}

export function create(): CameraLookAt {
  return {
    distance: 0,
    origin: { x: 0, y: 0, z: 0 },
    target: { x: 0, y: 0, z: 0 },
  };
}

export function clone(other: CameraLookAt): CameraLookAt {
  return {
    distance: other.distance,
    origin: { ...other.origin },
    target: { ...other.target },
  };
}

export function copy(self: CameraLookAt, other: CameraLookAt) {
  self.distance = other.distance;
  self.origin.x = other.origin.x;
  self.origin.y = other.origin.y;
  self.origin.z = other.origin.z;
  self.target.x = other.target.x;
  self.target.y = other.target.y;
  self.target.z = other.target.z;
}

export const setFromBounds = (() => {
  const RADIUS_SCALE = 1.2;
  const Z = new ThreeVector3(0, 0, 1);
  const origin = new ThreeVector3();

  return (
    self: CameraLookAt,
    bounds: Readonly<OrientedSphere | Sphere>,
    fov: number,
    aspectRatio: number,
  ) => {
    const fovRad = degreeToRadian(fov) * Math.min(aspectRatio, 1);
    const { center, radius } = bounds;
    const distance = (radius * RADIUS_SCALE) / Math.tan(fovRad * 0.5);
    const normal = (bounds as OrientedSphere).normal ?? Z;
    origin.copy(normal).multiplyScalar(distance).add(center);
    self.distance = distance;
    self.origin.x = origin.x;
    self.origin.y = origin.y;
    self.origin.z = origin.z;
    self.target.x = center.x;
    self.target.y = center.y;
    self.target.z = center.z;
  };
})();

export function createFromBounds(
  bounds: Readonly<Sphere>,
  fov: number,
  aspectRatio = 1,
): CameraLookAt {
  const ref = create();
  setFromBounds(ref, bounds, fov, aspectRatio);
  return ref;
}
